<template>
    <Tabs :selected-team="selectedTeam" :billing-addresses="billingAddresses" tab-key="members">
        <div class="mt-12 mb-4 flex items-center justify-end">
       
            <AtomicButton color="yellow" size="md" @click="showAddMemberModal = true">
                Add member
            </AtomicButton>
        </div>

        <DataTable
            :title="`Members of ${selectedTeam.name}`"
            searchable
            :headers="headers"
            :items="members"
        >
            <template #item-email_verified_at="{ data }">
                <div class="flex justify-center p-6">
                    <div v-if="data">
                        {{ format(new Date(data as string), 'd. L. yyyy') }}
                    </div>
                </div>
            </template>

            <template #item-custom="{ row }">
                <div class="flex w-full justify-center gap-2">
                    <AtomicButton
                        v-if="
                            !selectedTeam.personal_team &&
                            (row as IMemberRow).role !== 'owner'
                        "
                        color="white"
                        size="sm"
                        @click="handleMakeOwner(row as IMemberRow)"
                    >
                        <template #icon>
                            <KeyIcon class="h-6 w-6" />
                        </template>
                    </AtomicButton>
                    <AtomicButton
                        color="white"
                        size="sm"
                        @click="handleShowUser(row as IUser)"
                    >
                        <template #icon>
                            <MagnifyingGlassIcon class="h-6 w-6" />
                        </template>
                    </AtomicButton>
                </div>
            </template>
        </DataTable>
    </Tabs>

    <AddMemberModal
        v-if="showAddMemberModal"
        :team="selectedTeam"
        @close="showAddMemberModal = false"
    />
</template>
<script lang="ts" setup>
import IDataTableHeader from '@/components/Atomic/DataTable/types/IDataTableHeader';
import IPaginationData from '@/components/Atomic/DataTable/types/IPaginationData';
import ICurrentTeam from '@/types/Members/ICurrentTeam';
import IUser from '@/types/Members/IUser';
import DataTable from '@/components/Atomic/DataTable/DataTable.vue';
import { format } from 'date-fns';

import { KeyIcon, MagnifyingGlassIcon } from '@heroicons/vue/24/outline';

import Tabs from '../components/TeamTabs.vue';
import { router, usePage } from '@inertiajs/vue3';
import { route } from 'ziggy-js';
import AtomicButton from '@/components/Atomic/Buttons/AtomicButton.vue';
import IBillingAddress from '@/types/BillingAddresses/IBillingAddress';
import AddMemberModal from './AddMemberModal.vue';
import useConfirm from '@/composables/useConfirm';
import { create } from '@/composables/useNotifications';

interface IMemberRow extends IUser {
    role?: string;
}

const props = defineProps<{
    selectedTeam: ICurrentTeam;
    members: IPaginationData<IUser>;
    billingAddresses: IBillingAddress[];
}>();

const { show } = useConfirm();

const showAddMemberModal = ref(false);

const headers: IDataTableHeader[] = [
    {
        text: 'Name',
        id: 'name',
        sortable: true,
        sortableId: 'name',
    },
    {
        text: 'Email',
        id: 'email',
        sortable: true,
        sortableId: 'email',
    },
    {
        text: 'Role',
        id: 'role',
        sortable: true,
        sortableId: 'role',
    },
    {
        text: 'Joined',
        id: 'email_verified_at',
        align: 'center',
    },
    {
        text: '',
        id: 'custom',
    },
];

const handleShowUser = function (user: IUser) {
    router.get(
        route('kiosk.users.activity.index', {
            user: user.id,
        })
    );
};

const handleMakeOwner = async (user: IMemberRow) => {
    const confirmed = await show({
        header: `Make ${user.name} the team owner`,
        message: `The current owner will become an admin. Billing emails and trial status will follow the new owner. Are you sure?`,
        type: 'danger',
        confirm: 'Make owner',
    });

    if (confirmed) {
        makeOwner(user);
    }
};

const makeOwner = (user: IMemberRow) => {
    router.put(
        route('kiosk.teams.owner.update', {
            team: props.selectedTeam.id,
            user: user.id,
        }),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                const flash = usePage().props.flash as {
                    data?: { message?: string };
                };

                create({
                    title: flash?.data?.message || 'Team owner changed',
                    type: 'success',
                });
            },
            onError: (errors) => {
                create({
                    title:
                        Object.values(errors)[0] ||
                        'Failed to change team owner',
                    type: 'error',
                });
            },
        }
    );
};
</script>
